// kartenKlapp.tsx — Einklappen der Karten in der Kontaktanlage.
//
// Die Anlage wird oft ein zweites Mal durchgegangen; dann stören die schon
// fertigen Abschnitte. Jede Karte lässt sich deshalb links im Kopf einklappen.
//
// Gesteuert wird zentral über einen Kontext statt über Props an jeder Karte:
// Die `<SectionCard title="…">`-Tags der großen Formulare bleiben so wörtlich
// stehen (Quelltext-Tests hängen daran), und Kurzfassung bzw. Pflichtfeld-
// Hinweis kommen als Tabelle je Kartentitel aus dem Formular. Ohne Provider
// (Akte, Bereinigungsseiten) ist nichts klappbar — dort ändert sich nichts.

import * as React from "react";

/** Welche Karte umschließt diesen Inhalt? Setzt die SectionCard. */
export type KarteInfo = { id: string | null; innerhalb: boolean };
export const KarteContext = React.createContext<KarteInfo>({ id: null, innerhalb: false });

export type KartenKlapp = {
  istOffen: (id: string) => boolean;
  umschalten: (id: string) => void;
  oeffne: (id: string) => void;
  /** Aufklappen und in den Blick scrollen — nach einem gescheiterten Speichern. */
  zeige: (id: string) => void;
  alleEinklappen: () => void;
  alleAusklappen: () => void;
  /** true, solange mindestens eine bekannte Karte offen ist. */
  einigeOffen: boolean;
  /** Meldet eine Karte an; liefert die Abmeldung. */
  anmelden: (id: string) => () => void;
  kurzfassung: (id: string) => React.ReactNode;
  fehlt: (id: string) => boolean;
};

export const KartenKlappContext = React.createContext<KartenKlapp | null>(null);

const karteSelektor = (id: string) => `[data-karte="${id.replace(/["\\]/g, "\\$&")}"]`;

export function useKartenKlapp(
  opts: {
    kurzfassungen?: Record<string, React.ReactNode>;
    fehlt?: Record<string, boolean>;
    /**
     * Karten, die beim ersten Anzeigen zugeklappt sind — in der Akte die leeren
     * (kartenLeer.ts). Gilt nur einmalig; danach entscheidet der Nutzer.
     */
    startZu?: readonly string[];
  } = {},
): KartenKlapp {
  const [zu, setZu] = React.useState<ReadonlySet<string>>(() => new Set(opts.startZu ?? []));
  const [bekannt, setBekannt] = React.useState<ReadonlySet<string>>(() => new Set());

  // `startZu` gilt je Karte einmal: beim ersten Anzeigen (Initialwert) bzw.
  // beim ersten Anmelden — Titel, die erst nachladen (Rechtsform →
  // „Verantwortung für den Verein"), melden sich später an.
  const startZuRef = React.useRef(opts.startZu);
  startZuRef.current = opts.startZu;
  const gesehenRef = React.useRef<Set<string>>(new Set());

  const anmelden = React.useCallback((id: string) => {
    if (!gesehenRef.current.has(id)) {
      gesehenRef.current.add(id);
      if (startZuRef.current?.includes(id)) setZu((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
    }
    setBekannt((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
    return () =>
      setBekannt((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
  }, []);

  const umschalten = React.useCallback((id: string) => {
    setZu((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const oeffne = React.useCallback((id: string) => {
    setZu((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const zeige = React.useCallback(
    (id: string) => {
      oeffne(id);
      // Erst nach dem Aufklappen scrollen — vorher hat die Karte keine Höhe.
      requestAnimationFrame(() => {
        document
          .querySelector<HTMLElement>(karteSelektor(id))
          ?.scrollIntoView?.({ behavior: "smooth", block: "start" });
      });
    },
    [oeffne],
  );

  const kurzfassungen = opts.kurzfassungen ?? {};
  const fehlt = opts.fehlt ?? {};

  return {
    istOffen: (id) => !zu.has(id),
    umschalten,
    oeffne,
    zeige,
    alleEinklappen: () => setZu(new Set(bekannt)),
    alleAusklappen: () => setZu(new Set()),
    einigeOffen: [...bekannt].some((id) => !zu.has(id)),
    anmelden,
    kurzfassung: (id) => kurzfassungen[id] ?? null,
    fehlt: (id) => fehlt[id] === true,
  };
}
